import { ApolloError } from "@apollo/client";

export interface RateLimitInfo {
  retryAfterMs: number;
  retryAfterSeconds: number;
  message: string;
}

/**
 * Detects a RATE_LIMITED GraphQL error inside an Apollo error object.
 * Returns null if the error is not a rate-limit error.
 */
export function getRateLimitFromApolloError(
  err: unknown
): RateLimitInfo | null {
  if (!err) return null;
  const graphQLErrors = (err as ApolloError).graphQLErrors;
  if (!Array.isArray(graphQLErrors)) return null;
  for (const e of graphQLErrors) {
    if (e.extensions?.code === "RATE_LIMITED") {
      const ms =
        typeof e.extensions.retryAfterMs === "number"
          ? (e.extensions.retryAfterMs as number)
          : 0;
      return {
        retryAfterMs: ms,
        retryAfterSeconds: Math.max(1, Math.ceil(ms / 1000)),
        message: e.message,
      };
    }
  }
  return null;
}

/**
 * Detects a RATE_LIMITED REST response by inspecting the parsed body and
 * HTTP status. Returns null when the response is not a rate-limit error.
 */
export function getRateLimitFromRestBody(
  status: number,
  body: { error?: { code?: string; message?: string; retryAfterMs?: number } }
): RateLimitInfo | null {
  if (status !== 429) return null;
  const err = body?.error;
  if (!err || err.code !== "RATE_LIMITED") return null;
  const ms = typeof err.retryAfterMs === "number" ? err.retryAfterMs : 0;
  return {
    retryAfterMs: ms,
    retryAfterSeconds: Math.max(1, Math.ceil(ms / 1000)),
    message: err.message || "Rate limit exceeded",
  };
}

/** Human-friendly retry-after string (e.g. "30 seconds", "2 minutes"). */
export function formatRetryAfter(seconds: number): string {
  if (seconds <= 1) return "a moment";
  if (seconds < 60) return `${seconds} seconds`;
  const m = Math.ceil(seconds / 60);
  return m === 1 ? "1 minute" : `${m} minutes`;
}
